import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Layers, Plus } from 'lucide-react'
import {
  Button, Card, CardHeader, EmptyState, ErrorState, Field, FieldInput, Identifier,
  LoadingState, TBody, TD, TH, THead, Table, TableWrap, useLocale,
} from '@hk/ui'
import {
  ApiError, addCorridorPerFloor, fetchAreasForAdmin, fetchRoomAdmin, formatCount,
  formatMinutes, nameIn, saveArea, translateError,
  type AreaAdminRow, type AreaKind,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-127, THIRD TAB · THE HOTEL'S PUBLIC AREAS — Sprint 88, 26 October 2026.

   **THIS PRODUCT COULD NOT CREATE A PUBLIC AREA UNTIL TODAY, AND NOBODY NOTICED FOR
   FIVE SPRINTS.** W-135's go-live checklist has listed `PUBLIC_AREAS` as a BY_HAND step
   since Sprint 53 — meaning somebody types them — and there was no screen, no endpoint
   and no import that created one. Every area that had ever existed in this product was
   seeded into the demonstration database, so the demo looked complete and a real hotel
   reaching that row had no hands to use.

   It surfaced only when the client described their own building: *"each building each
   floor has passage that can be added as public areas."* Nineteen floors, nineteen
   passages, and no way to enter one.

   **IT IS A TAB ON W-127 RATHER THAN A SIDEBAR ENTRY**, and the approved pack drew it
   that way: W-127 is "Rooms and areas", with public areas as a further tab. The same
   permission guards all three — `admin.rooms` is "the shape of the hotel", and a corridor
   on the third floor is the same kind of fact about the same building as the rooms along
   it, written by the same person on the same afternoon. RoomSetup checks it once.

   **THE PASSAGE BUTTON IS THE POINT OF THE SCREEN.** Corridors come one per floor by the
   nature of a building; every other area is a one-off somebody names. Asking an
   administrator to fill nineteen nearly identical forms is how nineteen becomes
   seventeen — and the two that were missed are found by a guest, not by us.
   ========================================================================= */

const KINDS: AreaKind[] =
  ['FRONT_OF_HOUSE', 'CORRIDOR', 'DINING', 'FUNCTION', 'BACK_OF_HOUSE']

/** A blank area. A day's cadence, which is the model's own default — not a number
 *  invented on this screen. The client sets their real one. */
const BLANK = {
  areaId: null as string | null,
  code: '',
  name: '',
  nameAr: '',
  kind: 'FRONT_OF_HOUSE' as AreaKind,
  floorId: '',
  intervalMinutes: 1440,
  activeFromMinute: 0,
  activeToMinute: 0,
}

export function AreaSetup({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const queryClient = useQueryClient()
  const [form, setForm] = useState<typeof BLANK | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const areas = useQuery({
    queryKey: ['areas', 'admin', propertyId],
    queryFn: ({ signal }) => fetchAreasForAdmin(propertyId, { signal }),
  })

  /* THE FLOORS COME FROM THE ROOM MASTER, which is the only place they exist. A corridor
     has to name one, and a free-text floor would be a second spelling of a floor the
     hotel already has — the defect the room loader was built to avoid. */
  const master = useQuery({
    queryKey: ['rooms', 'admin', propertyId],
    queryFn: ({ signal }) => fetchRoomAdmin(propertyId, signal),
  })

  const floors = useMemo(() => {
    const buildings = new Map(
      (master.data?.buildings ?? []).map((b) => [b.buildingId, b.code]))
    /* **THE BUILDING IS IN THE LABEL.** This hotel has four third floors; a picker
       offering four entries all reading "3" is a picker nobody can use. Already in
       building-then-floor order from the server, so it is not re-sorted here. */
    return (master.data?.floors ?? []).map((f) => ({
      id: f.floorId,
      label: `${buildings.get(f.buildingId) ?? ''} ${f.code}`.trim(),
    }))
  }, [master.data])

  const done = () => {
    setForm(null)
    setProblem(null)
    void queryClient.invalidateQueries({ queryKey: ['areas'] })
  }

  const save = useMutation({
    mutationFn: () => saveArea(propertyId, {
      areaId: form!.areaId,
      code: form!.code,
      name: form!.name,
      nameAr: form!.nameAr.trim() === '' ? null : form!.nameAr,
      kind: form!.kind,
      floorId: form!.floorId === '' ? null : form!.floorId,
      intervalMinutes: form!.intervalMinutes,
      activeFromMinute: form!.activeFromMinute,
      activeToMinute: form!.activeToMinute,
    }),
    onSuccess: done,
    onError: (e) => setProblem(translateError(e as ApiError, locale).message),
  })

  const passages = useMutation({
    mutationFn: () => addCorridorPerFloor(propertyId, {
      intervalMinutes: 1440, activeFromMinute: 0, activeToMinute: 0,
    }),
    onSuccess: (result) => {
      setNote(result.created === 0
        ? t.areaPassagesNone
        : t.areaPassagesDone
            .replace('{created}', String(result.created))
            .replace('{already}', String(result.alreadyThere)))
      void queryClient.invalidateQueries({ queryKey: ['areas'] })
    },
    onError: (e) => setProblem(translateError(e as ApiError, locale).message),
  })

  if (areas.isLoading) return <LoadingState />
  if (areas.isError) return <ErrorState title={translateError(areas.error as ApiError, locale).message} />

  const rows = areas.data ?? []

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        <CardHeader icon={<Layers className="size-4" />} title={t.areaTitle} />
        <div className="p-4 pt-0 flex flex-col gap-3">
          <p className="text-[13px] text-muted-fg max-w-prose">{t.areaHint}</p>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => { setNote(null); setProblem(null); setForm({ ...BLANK }) }}
            >
              <Plus className="size-3.5" />
              {t.areaAdd}
            </Button>
            <Button
              variant="outline"
              disabled={passages.isPending || floors.length === 0}
              onClick={() => { setNote(null); setProblem(null); passages.mutate() }}
            >
              {t.areaPassages}
            </Button>
            <span className="text-[11.5px] text-muted-fg">{t.areaPassagesHint}</span>
          </div>

          {note && (
            <p role="status" className="text-[12.5px] text-foreground">{note}</p>
          )}
          {problem && (
            <p role="alert" className="rounded-hk bg-[#fef2f2] px-3 py-2 text-[12.5px] text-[#b91c1c]
                                       dark:bg-[#2b0f0f] dark:text-[#fca5a5]">{problem}</p>
          )}
        </div>
      </Card>

      {form && (
        <Card>
          <CardHeader
            icon={<Layers className="size-4" />}
            title={form.areaId ? t.areaEdit : t.areaAdd}
          />
          <form
            className="p-4 pt-0 grid gap-3 sm:grid-cols-2"
            onSubmit={(e) => { e.preventDefault(); save.mutate() }}
          >
            <Field label={t.areaCode}>
              {(p) => (
                <FieldInput
                  {...p}
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                />
              )}
            </Field>
            <Field label={t.areaKind}>
              {/* THE PLATFORM'S OWN SELECT, as everywhere else in this product: it is the
                  control a phone and a screen reader already know, in both languages. */}
              {(p) => (
                <select
                  {...p}
                  className="w-full bg-transparent border-0 outline-none h-6 text-sm text-foreground"
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value as AreaKind })}
                >
                  {KINDS.map((k) => (
                    <option key={k} value={k}>{t.areaKinds[k]}</option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={t.areaName}>
              {/* dir="auto" on every human-entered name — the August defect. */}
              {(p) => (
                <FieldInput
                  {...p}
                  dir="auto"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              )}
            </Field>
            <Field label={t.areaNameAr}>
              {(p) => (
                <FieldInput
                  {...p}
                  dir="auto"
                  value={form.nameAr}
                  onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
                />
              )}
            </Field>
            <Field label={t.areaFloor}>
              {(p) => (
                <select
                  {...p}
                  className="w-full bg-transparent border-0 outline-none h-6 text-sm text-foreground"
                  value={form.floorId}
                  onChange={(e) => setForm({ ...form, floorId: e.target.value })}
                >
                  <option value="">{t.areaNoFloor}</option>
                  {floors.map((f) => (
                    <option key={f.id} value={f.id}>{f.label}</option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={t.areaInterval} hint={t.areaIntervalHint}>
              {/* **NOT type="number".** A spinner in an Arabic interface is the control
                  this project has already ruled against on RoomEditor, for the same
                  reason: the arrows sit on the wrong side and the keypad is wrong. */}
              {(p) => (
                <FieldInput
                  {...p}
                  inputMode="numeric"
                  value={String(form.intervalMinutes)}
                  onChange={(e) => setForm({ ...form, intervalMinutes: Number(e.target.value) })}
                />
              )}
            </Field>
            <Field label={t.areaWindow} hint={t.areaWindowHint}>
              {(p) => (
                <div className="flex gap-2">
                  <FieldInput
                    {...p}
                    inputMode="numeric"
                    value={String(form.activeFromMinute)}
                    onChange={(e) => setForm({ ...form, activeFromMinute: Number(e.target.value) })}
                  />
                  <FieldInput
                    aria-label={t.areaWindow}
                    inputMode="numeric"
                    value={String(form.activeToMinute)}
                    onChange={(e) => setForm({ ...form, activeToMinute: Number(e.target.value) })}
                  />
                </div>
              )}
            </Field>
            <div className="sm:col-span-2 flex gap-2">
              <Button type="submit" disabled={save.isPending}>{t.areaSave}</Button>
              <Button type="button" variant="outline" onClick={() => setForm(null)}>
                {t.areaCancel}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card>
        {/* **A COUNT NEEDS A NOUN.** The first render of this screen headed the table
            with a bare "6" in both languages, which reads as an error rather than a
            heading — and in Arabic it was a Western digit on a page of Arabic-Indic
            ones. Visible only by looking at it. */}
        <CardHeader
          icon={<Layers className="size-4" />}
          title={t.areaCount.replace('{count}', formatCount(rows.length, locale))}
        />
        {rows.length === 0 ? (
          <EmptyState title={t.areaNone} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>{t.areaCode}</TH>
                  <TH>{t.areaName}</TH>
                  <TH>{t.areaKind}</TH>
                  <TH>{t.areaFloor}</TH>
                  <TH>{t.areaInterval}</TH>
                  <TH aria-label={t.areaEdit} />
                </tr>
              </THead>
              <TBody>
                {rows.map((a: AreaAdminRow) => (
                  <tr key={a.areaId}>
                    <TD><Identifier>{a.code}</Identifier></TD>
                    <TD><bdi>{nameIn(locale, a.name, a.nameAr)}</bdi></TD>
                    <TD>{t.areaKinds[a.kind] ?? a.kind}</TD>
                    <TD>
                      {a.floorCode
                        ? <bdi>{`${a.buildingCode ?? ''} ${a.floorCode}`.trim()}</bdi>
                        : <span className="text-muted-fg">{t.areaNoFloor}</span>}
                    </TD>
                    {/* **480 WHAT?** The column said "Clean every" and the cell said
                        "480" - minutes, but the page never said so, and in Arabic the
                        digits were Western. `formatMinutes` reads it as 8h. */}
                    <TD>{formatMinutes(a.intervalMinutes, locale)}</TD>
                    <TD>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setNote(null); setProblem(null)
                          setForm({
                            areaId: a.areaId, code: a.code, name: a.name,
                            nameAr: a.nameAr ?? '', kind: a.kind,
                            floorId: a.floorId ?? '',
                            intervalMinutes: a.intervalMinutes,
                            activeFromMinute: a.activeFromMinute,
                            activeToMinute: a.activeToMinute,
                          })
                        }}
                      >
                        {t.areaEdit}
                      </Button>
                    </TD>
                  </tr>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        )}
      </Card>
    </div>
  )
}
